import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageHeader from '@/components/base/PageHeader';
import MovieCard from '@/components/base/MovieCard';
import EmptyState from '@/components/base/EmptyState';
import HighlightText from '@/components/base/HighlightText';
import { formatDate, getTagList, getTopRatedMovies, searchMovies, searchNews } from '@/lib/queries';
import { useSeo } from '@/hooks/useSeo';

type TabKey = 'all' | 'movie' | 'news';

const tabs: { key: TabKey; label: string }[] = [
  { key: 'all', label: '全部结果' },
  { key: 'movie', label: '影片' },
  { key: 'news', label: '资讯' },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = (searchParams.get('q') ?? '').trim();
  const [input, setInput] = useState(keyword);
  const [tab, setTab] = useState<TabKey>('all');

  useEffect(() => {
    setInput(keyword);
    setTab('all');
  }, [keyword]);

  const movieResults = useMemo(() => searchMovies(keyword), [keyword]);
  const newsResults = useMemo(() => searchNews(keyword), [keyword]);

  useSeo({
    title: keyword ? `搜索「${keyword}」- 日韩成人电影` : '全站搜索 - 日韩成人电影',
    description: keyword
      ? `在日韩成人电影站内搜索「${keyword}」，共找到 ${movieResults.length} 部影片与 ${newsResults.length} 篇资讯。`
      : '使用关键词搜索站内影片与资讯，支持按片名、原名、导演、演员、标签与文章标题匹配。',
    keywords: keyword
      ? `${keyword},日韩成人电影搜索,${keyword}影片,${keyword}资讯`
      : '日韩成人电影搜索,影片搜索,资讯搜索',
  });

  const submit = (value: string) => {
    const next = value.trim();
    if (!next) {
      setSearchParams({});
      return;
    }
    setSearchParams({ q: next });
  };

  const total = movieResults.length + newsResults.length;
  const showMovies = tab === 'all' || tab === 'movie';
  const showNews = tab === 'all' || tab === 'news';
  const tagList = getTagList().slice(0, 8);
  const hotMovies = getTopRatedMovies(4);

  return (
    <>
      <PageHeader
        eyebrow="Search"
        title="全站搜索"
        description="输入片名、原名、导演、演员、标签或文章标题中的任意关键词，即可同时检索影片与资讯。搜索结果中的关键词会被高亮显示。"
        crumbs={[{ label: '搜索' }]}
      >
        <form
          className="mt-6 flex w-full max-w-2xl flex-col gap-2.5 sm:flex-row"
          onSubmit={(event) => {
            event.preventDefault();
            submit(input);
          }}
          role="search"
        >
          <div className="relative min-w-0 flex-1">
            <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-foreground-500" />
            <input
              type="search"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="试试「玻璃温室」「悬疑」「雨夜」"
              aria-label="搜索关键词"
              className="w-full rounded-md border border-background-700 bg-background-900 py-3 pl-10 pr-3 text-sm text-foreground-100 placeholder:text-foreground-600 focus:border-accent-500 focus:outline-none"
            />
          </div>
          <button
            type="submit"
            className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>
      </PageHeader>

      <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
        {!keyword ? (
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
            <div>
              <h2 className="text-lg font-bold text-foreground-50">从这些方向开始</h2>
              <p className="mt-2 text-sm text-foreground-500">热门标签与高分作品，点击即可跳转到对应的合集或详情页。</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {tagList.map((tag) => (
                  <Link
                    key={tag.name}
                    to={`/tag/${encodeURIComponent(tag.name)}`}
                    className="cursor-pointer rounded-full border border-background-700 bg-background-900 px-3.5 py-2 text-xs text-foreground-300 transition-colors hover:border-accent-500 hover:text-accent-400"
                  >
                    # {tag.name}
                    <span className="ml-1.5 font-label text-[10px] text-foreground-600">{tag.count}</span>
                  </Link>
                ))}
              </div>

              <h2 className="mt-10 text-lg font-bold text-foreground-50">高分作品</h2>
              <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-7 md:grid-cols-4">
                {hotMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>

            <aside className="rounded-lg border border-background-800 bg-background-900 p-5">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground-100">
                <i className="ri-lightbulb-line text-base text-accent-500" />
                搜索提示
              </h2>
              <ul className="mt-4 space-y-3 text-xs leading-relaxed text-foreground-500">
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 text-sm text-accent-500" />
                  支持中文片名、日文原名与英文名，例如「雨夜」「雨の夜の告白」。
                </li>
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 text-sm text-accent-500" />
                  可以搜索导演与演员姓名，例如「高濑和也」。
                </li>
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 text-sm text-accent-500" />
                  标签同样可以搜索，例如「反转结局」「城市夜景」。
                </li>
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 text-sm text-accent-500" />
                  资讯会匹配标题、栏目、摘要与作者。
                </li>
              </ul>
            </aside>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-background-800 bg-background-900 px-4 py-3.5 md:px-5">
              <p className="text-xs text-foreground-500">
                关键词 <span className="font-medium text-accent-400">「{keyword}」</span> 的搜索结果，共{' '}
                <span className="text-foreground-100">{total}</span> 条（影片 {movieResults.length} · 资讯{' '}
                {newsResults.length}）
              </p>
              <div className="flex gap-1 rounded-md border border-background-800 bg-background-950 p-1">
                {tabs.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setTab(item.key)}
                    className={`cursor-pointer whitespace-nowrap rounded px-3 py-1.5 text-xs transition-colors ${
                      tab === item.key
                        ? 'bg-accent-500 font-medium text-background-950'
                        : 'text-foreground-400 hover:text-foreground-100'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {total === 0 ? (
              <div className="mt-8">
                <EmptyState
                  icon="ri-search-eye-line"
                  title={`没有找到与「${keyword}」相关的内容`}
                  description="试试更短的关键词、换一个同义说法，或者直接从热门标签与分类页面继续浏览。"
                  primaryLabel="返回首页"
                  primaryTo="/"
                  onReset={() => {
                    setInput('');
                    setSearchParams({});
                  }}
                  resetLabel="清空关键词"
                />
                <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                  <span className="text-xs text-foreground-600">可以试试：</span>
                  {tagList.slice(0, 5).map((tag) => (
                    <button
                      key={tag.name}
                      type="button"
                      onClick={() => submit(tag.name)}
                      className="cursor-pointer rounded-full border border-background-700 px-3 py-1.5 text-[11px] text-foreground-400 transition-colors hover:border-accent-500 hover:text-accent-400"
                    >
                      {tag.name}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mt-8 space-y-10">
                {showMovies && (
                  <section>
                    <div className="flex items-end justify-between gap-3 border-b border-background-800 pb-3">
                      <h2 className="text-base font-bold text-foreground-50">影片结果</h2>
                      <span className="text-xs text-foreground-600">{movieResults.length} 部</span>
                    </div>
                    {movieResults.length === 0 ? (
                      <p className="mt-4 text-sm text-foreground-500">没有匹配的影片，试试切换到资讯标签。</p>
                    ) : (
                      <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 md:gap-x-5 lg:grid-cols-4 lg:gap-x-6">
                        {movieResults.map((movie) => (
                          <div key={movie.id}>
                            <MovieCard movie={movie} />
                            <p className="mt-2 line-clamp-2 text-[11px] leading-relaxed text-foreground-600">
                              <HighlightText text={movie.summary} keyword={keyword} />
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                )}

                {showNews && (
                  <section>
                    <div className="flex items-end justify-between gap-3 border-b border-background-800 pb-3">
                      <h2 className="text-base font-bold text-foreground-50">资讯结果</h2>
                      <span className="text-xs text-foreground-600">{newsResults.length} 篇</span>
                    </div>
                    {newsResults.length === 0 ? (
                      <p className="mt-4 text-sm text-foreground-500">没有匹配的资讯，试试切换到影片标签。</p>
                    ) : (
                      <div className="mt-2 divide-y divide-background-800">
                        {newsResults.map((article) => (
                          <Link key={article.id} to={`/news/${article.id}`} className="group flex cursor-pointer gap-4 py-4">
                            <div className="h-[72px] w-[110px] shrink-0 overflow-hidden rounded bg-background-800">
                              <img
                                src={article.cover}
                                alt={`${article.title} 配图`}
                                title={`${article.title} - 日韩成人电影资讯`}
                                className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                              />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2 text-[11px] text-foreground-600">
                                <span className="rounded bg-background-800 px-1.5 py-0.5 text-foreground-400">
                                  {article.section}
                                </span>
                                <span className="font-label tracking-[0.12em]">{formatDate(article.date)}</span>
                              </div>
                              <h3 className="mt-1.5 text-sm font-medium leading-relaxed text-foreground-100 transition-colors group-hover:text-accent-400">
                                <HighlightText text={article.title} keyword={keyword} />
                              </h3>
                              <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-500">
                                <HighlightText text={article.summary} keyword={keyword} />
                              </p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </section>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}